import { useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuth } from '../../components/AuthProvider';
import { loadSettings, saveSettings, clearSettingsCache, SETTINGS_DEFAULTS } from '../../lib/settings';
import type { AppSettings } from '../../lib/settings';
import { listAudit, AUDIT_PAGE_SIZE } from '../../api/audit';
import type { AuditRow } from '../../api/audit';
import MaestraTiposCampania from '../../components/MaestraTiposCampania';

const TZ = 'America/Lima';

interface Campo {
  key: keyof AppSettings;
  label: string;
  help: string;
  min: number;
  max: number;
}

const CAMPOS: Campo[] = [
  { key: 'lateToleranceMin', label: 'Tolerancia de tardanza (min)', help: 'Minutos después de la hora de inicio antes de marcar tardanza.', min: 0, max: 120 },
  { key: 'pdvRadiusM', label: 'Radio de geocerca por defecto (m)', help: 'Solo se usa como valor inicial al crear un PDV; el radio real es el de cada tienda.', min: 20, max: 5000 },
  { key: 'clockSkewMin', label: 'Desfase de reloj tolerado (min)', help: 'Diferencia máxima entre la hora del celular y la del servidor antes de marcar "hora sospechosa".', min: 1, max: 240 },
  { key: 'docWarnDays', label: 'Aviso de vencimiento de documentos (días)', help: 'Días de anticipación con que se avisa que un documento laboral vence.', min: 1, max: 365 },
];

export default function Ajustes() {
  const { roles } = useAuth();
  const isAdmin = roles.includes('admin');

  const [tab, setTab] = useState<'parametros' | 'tipos' | 'auditoria'>('parametros');
  const [values, setValues] = useState<AppSettings>(SETTINGS_DEFAULTS);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    loadSettings().then((s) => {
      setValues(s);
      setTexts({
        lateToleranceMin: String(s.lateToleranceMin),
        pdvRadiusM: String(s.pdvRadiusM),
        clockSkewMin: String(s.clockSkewMin),
        docWarnDays: String(s.docWarnDays),
      });
    });
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || tab !== 'auditoria') return;
    listAudit(page)
      .then(({ rows, hasMore: more }) => { setAudit(rows); setHasMore(more); })
      .catch((e) => setMessage('Error al cargar la auditoría: ' + (e instanceof Error ? e.message : String(e))));
  }, [isAdmin, tab, page]);

  function onChange(campo: Campo, text: string) {
    setTexts((t) => ({ ...t, [campo.key]: text }));
    const n = Number(text);
    let error = '';
    if (text.trim() === '' || !Number.isFinite(n)) error = 'Debe ser un número.';
    else if (!Number.isInteger(n)) error = 'Debe ser un número entero.';
    else if (n < campo.min || n > campo.max) error = `Debe estar entre ${campo.min} y ${campo.max}.`;
    setErrors((e) => ({ ...e, [campo.key]: error }));
    if (!error) setValues((v) => ({ ...v, [campo.key]: n }));
  }

  const hayErrores = CAMPOS.some((c) => (errors[c.key] ?? '') !== '');

  async function guardar() {
    if (hayErrores) return;
    setSaving(true);
    setMessage('');
    try {
      await saveSettings(values);
      clearSettingsCache();
      setMessage('Parámetros guardados. Los cambios aplican al siguiente marcado.');
    } catch (e) {
      setMessage('Error al guardar: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Ajustes</h1>
        <p className="mt-2 text-sm text-gray-600">
          Solo el jefe de operaciones (rol admin) puede ver y cambiar los parámetros del sistema.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="mb-4 text-xl font-semibold">Ajustes</h1>

      <div className="mb-4 flex gap-2">
        <button onClick={() => setTab('parametros')}
          className={`rounded px-3 py-1 text-sm ${tab === 'parametros' ? 'bg-blue-600 text-white' : 'border'}`}>
          Parámetros
        </button>
        <button onClick={() => setTab('tipos')}
          className={`rounded px-3 py-1 text-sm ${tab === 'tipos' ? 'bg-blue-600 text-white' : 'border'}`}>
          Tipos de campaña
        </button>
        <button onClick={() => { setTab('auditoria'); setPage(0); }}
          className={`rounded px-3 py-1 text-sm ${tab === 'auditoria' ? 'bg-blue-600 text-white' : 'border'}`}>
          Auditoría
        </button>
      </div>

      {message && <p className="mb-3 text-sm text-gray-700">{message}</p>}

      {tab === 'tipos' && <MaestraTiposCampania />}

      {tab === 'parametros' && (
        <section className="max-w-xl space-y-4">
          {CAMPOS.map((c) => (
            <div key={c.key}>
              <label className="block text-sm font-medium text-gray-700" htmlFor={c.key}>
                {c.label}
              </label>
              <input
                id={c.key}
                type="number"
                inputMode="numeric"
                className="mt-1 w-40 rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={texts[c.key] ?? ''}
                onChange={(e) => onChange(c, e.target.value)}
              />
              <p className="text-xs text-gray-500">{c.help}</p>
              {errors[c.key] && <p className="text-xs text-red-600">{errors[c.key]}</p>}
            </div>
          ))}
          <button onClick={guardar} disabled={saving || hayErrores}
            className="rounded bg-blue-600 px-4 py-1.5 text-white disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </section>
      )}

      {tab === 'auditoria' && (
        <section>
          <div className="overflow-x-auto">
            <table className="min-w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2">Fecha</th>
                  <th className="p-2 text-left">Actor</th>
                  <th className="p-2 text-left">Acción</th>
                  <th className="p-2 text-left">Entidad</th>
                  <th className="p-2 text-left">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((r) => (
                  <tr key={r.id} className="border-t align-top">
                    <td className="p-2 text-center whitespace-nowrap">
                      {formatInTimeZone(r.created_at, TZ, 'dd/MM/yyyy HH:mm')}
                    </td>
                    <td className="p-2">
                      {r.actor ? `${r.actor.first_names} ${r.actor.last_names}` : '—'}
                    </td>
                    <td className="p-2">{r.action}</td>
                    <td className="p-2">
                      {r.entity}
                      {r.entity_id && (
                        <span className="block text-xs text-gray-400">{r.entity_id.slice(0, 8)}…</span>
                      )}
                    </td>
                    <td className="p-2">
                      {r.detail ? (
                        <details>
                          <summary className="cursor-pointer text-blue-600">Ver detalle</summary>
                          <pre className="mt-1 max-w-md overflow-x-auto rounded bg-gray-50 p-2 text-xs">
                            {JSON.stringify(r.detail, null, 2)}
                          </pre>
                        </details>
                      ) : ('—')}
                    </td>
                  </tr>
                ))}
                {audit.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-gray-500">
                      Sin registros de auditoría.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="rounded border px-3 py-1 disabled:opacity-50">
              Anterior
            </button>
            <span>Página {page + 1} · {AUDIT_PAGE_SIZE} por página</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={!hasMore}
              className="rounded border px-3 py-1 disabled:opacity-50">
              Siguiente
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
